import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { Subject, delay, takeUntil } from 'rxjs';

import { ScrolledPastService } from './scrolled-past.service';

/**
 * Emits event in (scrolledPast) when the element is in viewport
 * or scrolled past.
 *
 * By default fires when any part of the element is visible (threshold 0).
 * Set [finScrolledPastThreshold]="1" to fire only when the element is fully visible.
 */
@Directive({
  selector: '[finScrolledPast]',
  standalone: false,
})
export class ScrolledPastDirective implements AfterViewInit, OnDestroy {
  private el = inject<ElementRef<HTMLElement>>(ElementRef);
  private scrolledPastService = inject(ScrolledPastService);
  private platform = inject(PLATFORM_ID);

  /**
   * Proportion of the element that must be visible in the viewport before
   * (finScrolledPast) is emitted.
   *
   * Valid values are in the range [0, 1], where:
   * - 0 means any part of the element being visible is enough.
   * - 1 means the element must be fully visible.
   */
  get finScrolledPastThreshold(): number | undefined {
    return this._finScrolledPastThreshold;
  }

  @Input()
  set finScrolledPastThreshold(value: number | null | undefined) {
    if (value == null) {
      // no custom threshold: fall back to the ScrolledPastService-based behaviour.
      this._finScrolledPastThreshold = undefined;
      return;
    }

    // enforce [0, 1] range by clamping out of range values.
    const clamped = Math.min(Math.max(value, 0), 1);
    this._finScrolledPastThreshold = clamped;
  }

  private _finScrolledPastThreshold: number | undefined;
  @Output()
  finScrolledPast: EventEmitter<boolean> = new EventEmitter<boolean>();

  private ngDestroyed$ = new Subject<void>();
  /**
   * IntersectionObserver instance used when a custom threshold is specified via
   * `finScrolledPastThreshold`. In that case, this observer provides the
   * threshold based intersection detection as an alternative to the
   * {@link ScrolledPastService} used in the default path.
   */
  private observer: IntersectionObserver;

  ngAfterViewInit(): void {
    if (isPlatformServer(this.platform)) {
      return;
    }
    this.listenToIntersectionEvents();
  }

  ngOnDestroy = (): void => {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
    this.observer?.disconnect();
  };

  private listenToIntersectionEvents() {
    if (this.finScrolledPastThreshold != null) {
      this.observeWithThreshold();
    } else {
      this.observeWithService();
    }
  }

  private observeWithService(): void {
    const events$ = this.scrolledPastService.listenToElement(this.el.nativeElement);
    // Delay because we need to run in the following change detection cycle
    events$.pipe(delay(1), takeUntil(this.ngDestroyed$)).subscribe(() => {
      this.finScrolledPast.emit(true);
      this.finScrolledPast.complete();
    });
  }

  private observeWithThreshold(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find((e) => e.target === this.el.nativeElement && e.isIntersecting);
        if (entry) {
          this.observer.disconnect();
          this.finScrolledPast.emit(true);
          this.finScrolledPast.complete();
        }
      },
      { threshold: this.finScrolledPastThreshold }
    );
    this.observer.observe(this.el.nativeElement);
  }
}
